import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  TrendingUp,
  LayoutDashboard,
  BarChart3,
  Bot,
  Store,
  ArrowDownToLine,
  ArrowUpFromLine,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Shield,
  PieChart,
  Zap,
  Globe,
  Users,
  Landmark
} from 'lucide-react';
import MarketTicker from './MarketTicker';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/markets', label: 'Markets', icon: BarChart3 },
  { path: '/live-markets', label: 'Live Markets', icon: Globe },
  { path: '/trading', label: 'Trade', icon: Zap },
  { path: '/analytics', label: 'Analytics', icon: PieChart },
  { path: '/ai-trading', label: 'AI Trading', icon: Bot },
  { path: '/business', label: 'Businesses', icon: Store },
  { path: '/social', label: 'Social', icon: Users, badge: 'NEW' },
  { path: '/rockefeller-method', label: 'Rockefeller Method', icon: Landmark },
];

const financeItems = [
  { path: '/deposit', label: 'Deposit', icon: ArrowDownToLine },
  { path: '/withdraw', label: 'Withdraw', icon: ArrowUpFromLine },
  { path: '/transactions', label: 'Transactions', icon: History },
];

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatBalance = (balance) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(balance || 0);
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#00E676] rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.4)]">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl text-white hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Sraivest
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden 2xl:flex items-center gap-1">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className={`gap-2 ${
                      location.pathname === item.path
                        ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                    {item.badge && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-500 text-white rounded">
                        {item.badge}
                      </span>
                    )}
                  </Button>
                </Link>
              ))}

              {/* Finance Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="gap-2 text-gray-300 hover:text-white hover:bg-white/10"
                    data-testid="nav-finance-dropdown"
                  >
                    <ArrowDownToLine className="w-4 h-4" />
                    Finance
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#0A0A0A] border-white/15" align="end">
                  {financeItems.map((item) => (
                    <DropdownMenuItem 
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="text-gray-200 hover:text-white focus:text-white hover:bg-white/10 cursor-pointer"
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Balance */}
              <div className="hidden sm:block text-right px-3 py-1.5 bg-[#00E676]/10 rounded-lg border border-[#00E676]/20">
                <p className="text-xs text-gray-400">Balance</p>
                <p className="font-bold text-[#00E676] number text-lg" data-testid="nav-balance">
                  {formatBalance(user?.balance)}
                </p>
              </div>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="gap-2 text-gray-200 hover:bg-white/10"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center overflow-hidden border border-white/20">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                    </div>
                    <span className="hidden md:block font-medium">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3 hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#0A0A0A] border-white/15 w-56" align="end">
                  <div className="px-3 py-2 border-b border-white/10">
                    <p className="font-semibold text-white">{user?.name}</p>
                    <p className="text-sm text-gray-400">{user?.email}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate('/settings')}
                    className="text-gray-200 hover:text-white focus:text-white hover:bg-white/10 cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  {user?.is_admin && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="text-[#818cf8] hover:text-[#a5b4fc] focus:text-[#a5b4fc] hover:bg-[#6366F1]/10 cursor-pointer"
                      data-testid="admin-link"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-400 hover:text-red-300 focus:text-red-300 hover:bg-red-500/10 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="2xl:hidden text-gray-200 shrink-0"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="2xl:hidden border-t border-white/10 bg-[#050505] animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4 space-y-2">
              {[...navItems, ...financeItems].map((item) => (
                <Link 
                  key={item.path} 
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant="ghost"
                    className={`w-full justify-start gap-3 ${
                      location.pathname === item.path
                        ? 'bg-[#00E676]/20 text-[#00E676]'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Button>
                </Link>
              ))}
          <div className="mt-4 border-t border-white/10 pt-4">
            <div className="mb-3 flex items-center gap-3 px-3">
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-[#00E676]/20 flex items-center justify-center text-[#00E676]">
                {user?.picture ? (
                  <img src={user.picture} alt={user?.name || 'Profile'} className="h-full w-full object-cover" />
                ) : (
                  <span className="font-bold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate font-semibold text-white">{user?.name || 'My Account'}</p>
                <p className="truncate text-sm text-gray-400">{user?.email}</p>
              </div>
            </div>
            <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start gap-3 text-gray-300 hover:text-white hover:bg-white/10">
                <Settings className="h-5 w-5" /> Profile & Settings
              </Button>
            </Link>
            {user?.is_admin && (
              <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start gap-3 text-[#818cf8] hover:text-[#a5b4fc] hover:bg-[#6366F1]/10">
                  <Shield className="h-5 w-5" /> Admin Dashboard
                </Button>
              </Link>
            )}
            <Button variant="ghost" onClick={handleLogout} className="w-full justify-start gap-3 text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="mobile-logout-btn">
              <LogOut className="h-5 w-5" /> Log out
            </Button>
          </div>
            </div>
          </div>
        )}
      </nav>

      {/* Market Ticker */}
      <div className="fixed top-16 left-0 right-0 z-40">
        <MarketTicker />
      </div>

      {/* Main Content */}
      <main className="pt-28 pb-8 px-4 md:px-6">
        <div className="max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;
